import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import usePlanStore from '../../../../../store/planStore';

interface ComparedPlansBarProps {
  onCompare?: (ids: string[]) => void;
}

function ComparedPlansBar({ onCompare }: ComparedPlansBarProps) {
  const { palette } = useTheme();
  const { comparedPlans, removeComparedPlan } = usePlanStore((state) => ({
    comparedPlans: state.comparedPlans,
    removeComparedPlan: state.removeComparedPlan,
  }));

  const entries = Object.entries(comparedPlans);
  const hasItems = entries.length > 0;

  if (!hasItems) return null;

  const planIds = entries.map(([id]) => id);

  const handleCompare = () => {
    if (onCompare) onCompare(planIds);
  };

  return (
    <Box
      mt={2}
      py={1.5}
      px={2}
      position="fixed"
      bottom={0}
      left={0}
      width="calc(100vw - 44px)"
      sx={{
        border: `1px solid ${palette.divider}`,
        backgroundColor: palette.background.paper,
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {entries.length === 1 ? (
        <Stack flexDirection="row" alignItems="center">
          <WarningIcon color="error" />
          <Typography
            fontSize="12px"
            fontWeight={600}
            color="error"
            width="318px"
            pl="8px"
          >
            Select at least 2 plans to activate compare function <br /> (max 3
            plans can be compared)
          </Typography>
        </Stack>
      ) : (
        <Box />
      )}

      <Box
        sx={{
          maxWidth: '100%',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            maxWidth: '100%',
            justifyContent: 'center',
          }}
        >
          {entries.map(([id, body]) => {
            const name = (body as any)?.planName || id;

            return (
              <Box
                key={id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 0.75,
                  borderRadius: '5px',
                  border: `1px solid #C4C4C4`,
                  backgroundColor: palette.background.default,
                  whiteSpace: 'nowrap',

                  flex: '1 1 364px',
                  maxWidth: 364,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body2"
                  title={name}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mr: 1,
                  }}
                >
                  {name}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => removeComparedPlan(id)}
                  sx={{ flexShrink: 0 }}
                >
                  <RemoveCircleOutlineIcon color="error" fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ justifySelf: 'end' }}>
        <Button
          variant="contained"
          onClick={handleCompare}
          disabled={planIds.length < 2}
          sx={{
            minWidth: '117px',
            fontWeight: 700,
            fontSize: '14px',
            paddingX: 0,
          }}
        >
          Compare plans
        </Button>
      </Box>
    </Box>
  );
}

export default ComparedPlansBar;
